import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Typography, Breadcrumbs, Link, Box, Fab, Tooltip, Paper, Divider } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon } from '@mui/icons-material';
import API from '@utils/axios';
import Layout from '@components/Layout';
import PackTable from '@components/pack/PackTable';
import PackCreationDialog from '@components/pack/PackCreationDialog';
import PackEditDialog from '@components/pack/PackEditDialog';
import PackHistoryDialog from '@components/pack/PackHistoryDialog';
import CustomerTabs from '@components/customer/CustomerTabs';
import CustomerThresholds from '@components/customer/CustomerThresholds';
import CustomerRebalance from '@components/customer/CustomerRebalance';

function PackListPage() {
    const { t } = useTranslation();
    const { customerId, type } = useParams();
    const [customer, setCustomer] = useState(null);
    const [modalCreateOpen, setModalCreateOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedPack, setSelectedPack] = useState(null);
    const tableRef = useRef();

    const fetchCustomer = useCallback(async () => {
        const response = await API.get(`/customers/${customerId}`);
        setCustomer(response.data);
    }, [customerId]);

    useEffect(() => {
        fetchCustomer();
    }, [customerId, fetchCustomer]);

    const refreshAll = () => {
        if (tableRef.current) {
            tableRef.current.refresh();
        }
        fetchCustomer();
    };

    const handleEdit = (pack) => {
        setSelectedPack(pack);
        setEditModalOpen(true);
    };

    const handleHistory = (pack) => {
        setSelectedPack(pack);
        setHistoryModalOpen(true);
    };

    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to="/customers" underline="hover">{t('Customer management')}</Link>
                    <Typography>{customer?.name}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to="/customers" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            <CustomerTabs customer={customer} currentTab={type} />
            <PackTable
                ref={tableRef}
                customerId={customerId}
                type={type}
                onEdit={handleEdit}
                onHistory={handleHistory}
            />

            {customer && (
                <Paper sx={{ mt: 3, p: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'center', gap: 3 }}>
                        <Box sx={{ flex: 1, pt: 2 }}>
                            <CustomerThresholds
                                customer={customer}
                                type={type}
                                onSuccess={fetchCustomer}
                            />
                        </Box>
                        <Divider orientation="vertical" sx={{ display: { xs: 'none', lg: 'block' } }} flexItem />
                        <Divider orientation="horizontal" sx={{ display: { xs: 'block', lg: 'none' } }} flexItem />
                        <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: '250px' }}>
                            <CustomerRebalance customerId={customerId} type={type} refreshAll={refreshAll} />
                        </Box>
                    </Box>
                </Paper>
            )}

            <Tooltip title={t('Add {{type}} credits', { type: t(`credit.${type}`) })}
                placement="top" arrow>
                <Fab color="primary" aria-label={t('Add {{type}} credits', { type: t(`credit.${type}`) })}
                    className="main-fab" sx={{ boxShadow: 6 }}
                    onClick={() => setModalCreateOpen(true)}>
                    <AddIcon />
                </Fab>
            </Tooltip>

            <PackCreationDialog
                customerId={customerId}
                type={type}
                modalCreateOpen={modalCreateOpen}
                setModalCreateOpen={setModalCreateOpen}
                onSuccess={refreshAll}
            />

            {selectedPack && (
                <PackEditDialog
                    modalOpen={editModalOpen}
                    setModalOpen={setEditModalOpen}
                    customerId={customerId}
                    type={type}
                    packId={selectedPack.id}
                    initialData={selectedPack}
                    onSuccess={refreshAll}
                />
            )}

            {selectedPack && (
                <PackHistoryDialog
                    modalOpen={historyModalOpen}
                    setModalOpen={setHistoryModalOpen}
                    customerId={customerId}
                    packId={selectedPack.id}
                />
            )}

        </Layout>
    );
}

export default PackListPage;